import { unwrapResult } from '@reduxjs/toolkit';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  getUser as getUserAction,
  login as loginAction,
  logout as logoutAction
} from '../redux/authSlice';

const AuthContext = React.createContext();

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const state = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const login = useCallback(async (data) => {
    return unwrapResult(await dispatch(loginAction(data)));
  }, []);

  const logout = useCallback(() => {
    dispatch(logoutAction());
  }, []);

  const getUser = useCallback(async () => {
    return unwrapResult(await dispatch(getUserAction()));
  }, []);

  const roles = useMemo(() => {
    if (!state.user) return new Set();
    const roleSet = new Set();
    if (state.user.isRoot) roleSet.add('root');
    state.user.tenants?.forEach((t) => roleSet.add(t.role));
    return roleSet;
  }, [state.user]);

  const hasRole = (...roleKeys) => {
    if (roles.has('root')) return true;
    return roleKeys.some((key) => roles.has(key));
  };

  useEffect(() => {
    getUser().catch(() => null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user: state.user,
        roles,
        loading: state.loading,
        isLoggedIn: state.isLoggedIn,
        login,
        logout,
        getUser,
        hasRole
      }}>
      {children}
    </AuthContext.Provider>
  );
}
